import { useEffect, useRef } from "react";
import api from "../api/axios";
import {
  ensureNativeAccessToken,
  exchangeNativeOAuthCode,
  refreshNativeAccessToken,
} from "../native/auth";
import {
  getInternalPathFromAppUrl,
  isNativeApp,
  isVaultGridHttpsUrl,
  openExternalUrl,
} from "../native/runtime";
import { normalizeAttendanceClaimPath } from "../native/attendanceClaimRoute";
import {
  CHAT_LIST_PATH,
  buildChatListNavigationOptions,
  getChatListTabForPath,
  isChatsPath,
} from "../utils/chatNavigation";

const ROOT_PATHS = new Set(["/", "/auth", "/events/search", "/vault/main"]);
const INTERNAL_CHAT_DETAIL_PATH_RE = /^\/menu\/chats\/(?:direct|event|group|support)(?:\/|$)/;
const EVENT_DETAIL_PATH_RE = /^\/events\/(?:.*-)?(\d+)\/?$/;
const ACTIONABLE_HOST_ROLE_PUSH_KINDS = new Set([
  "event_cohost_request",
  "event_host_transfer_request",
]);

const isCashDepositReturnPath = (path) => {
  try {
    const url = new URL(String(path || ""), "https://vaultgrid.net");
    return url.pathname === "/events/create"
      && url.searchParams.get("shop_payment_source") === "host_paid_cash_event_deposit"
      && Number(url.searchParams.get("shop_payment")) > 0;
  } catch {
    return false;
  }
};

export function shouldMarkPushNotificationRead(detail) {
  const actionKind = String(detail?.action_kind || "").trim().toLowerCase();
  const requestStatus = String(detail?.request_status || "pending").trim().toLowerCase();
  return !(
    ACTIONABLE_HOST_ROLE_PUSH_KINDS.has(actionKind)
    && requestStatus === "pending"
  );
}

export function parseNativeAuthUrl(rawUrl) {
  try {
    const url = new URL(String(rawUrl || ""));
    if (url.protocol !== "net.vaultgrid.app:" || url.hostname !== "auth") return null;
    return {
      provider: url.pathname.replace(/^\//, ""),
      code: url.searchParams.get("code"),
      error: url.searchParams.get("error"),
      gToken: url.searchParams.get("g_token"),
      next: url.searchParams.get("next") || "/events/search",
    };
  } catch {
    return null;
  }
}

export function getPushDestination(detail) {
  const rawRoute = String(detail?.route || detail?.action_url || "").trim();
  const internalPath = getInternalPathFromAppUrl(rawRoute);
  const kind = String(detail?.kind || "").trim().toLowerCase();
  const chatId = Number(detail?.chat_id || 0);
  const messageId = Number(detail?.message_id || 0);
  const isChatMessage = kind === "chat_message" || (
    Number.isInteger(chatId) && chatId > 0 && Number.isInteger(messageId) && messageId > 0
  );

  if (isChatMessage) {
    if (internalPath && INTERNAL_CHAT_DETAIL_PATH_RE.test(internalPath)) {
      return normalizeAttendanceClaimPath(internalPath);
    }

    const chatType = String(detail?.chat_type || "").trim().toLowerCase();
    const eventIdFromPayload = Number(detail?.event_id || 0);
    let eventIdFromRoute = 0;
    if (internalPath) {
      try {
        const parsed = new URL(internalPath, "https://vaultgrid.invalid");
        eventIdFromRoute = Number(parsed.pathname.match(EVENT_DETAIL_PATH_RE)?.[1] || 0);
      } catch {
        eventIdFromRoute = 0;
      }
    }
    const eventId = Number.isInteger(eventIdFromPayload) && eventIdFromPayload > 0
      ? eventIdFromPayload
      : eventIdFromRoute;

    // Older persisted chat pushes could contain the public event URL. A chat
    // notification must still open the conversation, never the event card.
    if (eventId > 0 && (!chatType || chatType === "event")) {
      return `/menu/chats/event/${eventId}`;
    }
    if (Number.isInteger(chatId) && chatId > 0) {
      if (["community", "group"].includes(chatType)) {
        return `/menu/chats/group/${chatId}`;
      }
      if (chatType === "support") {
        return `/menu/chats/support/${chatId}`;
      }
      if (chatType === "direct") {
        return `/menu/chats/direct/id/${chatId}`;
      }
    }
  }

  return internalPath
    ? normalizeAttendanceClaimPath(internalPath)
    : "/vault/main?open_mailbox=1";
}

export function getPushNavigationOptions(detail, destination) {
  const chatId = Number(detail?.chat_id || 0);
  if (
    !Number.isInteger(chatId) ||
    chatId <= 0 ||
    !INTERNAL_CHAT_DETAIL_PATH_RE.test(String(destination || ""))
  ) {
    return null;
  }

  return {
    state: {
      focusFirstUnread: true,
      pushChatId: chatId,
      pushMessageId: Number(detail?.message_id || 0) || null,
      pushNotificationId: Number(detail?.notification_id || 0) || null,
    },
  };
}

export default function useNativeAppRuntime({ navigate, location }) {
  const navigateRef = useRef(navigate);
  const pathnameRef = useRef(location?.pathname || "/");

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    pathnameRef.current = location?.pathname || "/";
  }, [location?.pathname]);

  useEffect(() => {
    if (!isNativeApp()) return undefined;

    let disposed = false;
    const handles = [];
    let lastHandledUrl = "";
    let lastHandledUrlAt = 0;

    const prepareProtectedChatNavigation = async (destination) => {
      if (!isChatsPath(destination)) return;
      await refreshNativeAccessToken({ force: true }).catch((error) => {
        console.warn("Native session refresh before chat navigation failed", error);
        return ensureNativeAccessToken().catch(() => null);
      });
    };

    const install = async () => {
      const [{ App }, { Browser }] = await Promise.all([
        import("@capacitor/app"),
        import("@capacitor/browser"),
      ]);
      if (disposed) return;

      handles.push(await Browser.addListener("browserFinished", () => {
        window.dispatchEvent(new Event("vg:native-resume"));
      }));

      const handleAppUrl = async ({ url }) => {
        const rawUrl = String(url || "");
        const now = Date.now();
        if (rawUrl && rawUrl === lastHandledUrl && now - lastHandledUrlAt < 2000) return;
        lastHandledUrl = rawUrl;
        lastHandledUrlAt = now;

        const nativeAuth = parseNativeAuthUrl(rawUrl);
        if (nativeAuth) {
          await Browser.close().catch(() => null);
          if (nativeAuth.code) {
            try {
              await exchangeNativeOAuthCode(nativeAuth.code);
              window.dispatchEvent(new CustomEvent("vg:native-oauth-complete", {
                detail: { next: nativeAuth.next },
              }));
            } catch (error) {
              window.dispatchEvent(new CustomEvent("vg:native-oauth-error", {
                detail: { error: error?.message || "oauth_failed" },
              }));
            }
            return;
          }
          if (nativeAuth.gToken) {
            const params = new URLSearchParams({
              tab: "register",
              mode: "google",
              g_token: nativeAuth.gToken,
            });
            if (nativeAuth.next) params.set("next", nativeAuth.next);
            navigateRef.current(`/auth?${params.toString()}`, { replace: true });
            return;
          }
          window.dispatchEvent(new CustomEvent("vg:native-oauth-error", {
            detail: { error: nativeAuth.error || "oauth_failed" },
          }));
          return;
        }

        const internalPath = getInternalPathFromAppUrl(rawUrl);
        if (internalPath) {
          const destination = normalizeAttendanceClaimPath(internalPath);
          await prepareProtectedChatNavigation(destination);
          if (disposed) return;
          if (isCashDepositReturnPath(destination)) {
            await Browser.close().catch(() => null);
          }
          navigateRef.current(destination);
          return;
        }

        // A stale AASA/App Links cache may still launch the app for a web-only
        // VaultGrid URL. Hand it back to the system browser instead of leaving
        // the user on an unrelated or unsupported in-app route.
        if (isVaultGridHttpsUrl(rawUrl)) {
          await openExternalUrl(rawUrl).catch((error) => {
            console.warn("Failed to return web-only VaultGrid link to browser", error);
          });
        }
      };

      handles.push(await App.addListener("appUrlOpen", handleAppUrl));

      // appUrlOpen covers an already-running app; getLaunchUrl covers a cold
      // start caused by an OAuth callback, Universal Link or Android App Link.
      const launchUrl = await App.getLaunchUrl();
      if (!disposed && launchUrl?.url) {
        await handleAppUrl(launchUrl);
      }

      handles.push(await App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) {
          window.dispatchEvent(new CustomEvent("vg:native-app-state", {
            detail: { isActive: false },
          }));
          return;
        }
        ensureNativeAccessToken().finally(() => {
          window.dispatchEvent(new CustomEvent("vg:native-app-state", {
            detail: { isActive: true },
          }));
          window.dispatchEvent(new Event("vg:native-resume"));
        });
      }));

      handles.push(await App.addListener("backButton", () => {
        const path = pathnameRef.current;
        const chatTab = getChatListTabForPath(path);
        if (chatTab) {
          navigateRef.current(
            CHAT_LIST_PATH,
            buildChatListNavigationOptions({ tab: chatTab })
          );
          return;
        }
        if (ROOT_PATHS.has(path)) {
          App.exitApp();
          return;
        }
        navigateRef.current(-1);
      }));
    };

    install().catch((error) => {
      console.warn("Native app runtime initialization failed", error);
    });

    const handlePushTap = async (event) => {
      const detail = event?.detail || {};
      // A push tap commonly races the app's foreground transition. Refresh the
      // native session before mounting a protected chat route so its access
      // check cannot fail with the stale token retained while the app slept.
      await refreshNativeAccessToken({ force: true }).catch((error) => {
        console.warn("Native session refresh before push navigation failed", error);
        return ensureNativeAccessToken().catch(() => null);
      });
      if (disposed) return;

      const notificationId = Number(detail.notification_id);
      if (
        Number.isInteger(notificationId)
        && notificationId > 0
        && shouldMarkPushNotificationRead(detail)
      ) {
        api.post(`/notifications/${notificationId}/read/`, undefined, {
          meta: { suppressGlobalError: true },
        }).catch(() => null);
      }
      const destination = getPushDestination(detail);
      const navigationOptions = getPushNavigationOptions(detail, destination);
      if (navigationOptions) {
        navigateRef.current(destination, navigationOptions);
      } else {
        navigateRef.current(destination);
      }
    };
    window.addEventListener("vg:push-notification-tapped", handlePushTap);

    const handleExternalLinkClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = event.target?.closest?.("a[href]");
      if (!anchor || anchor.hasAttribute("download")) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      const externalProtocols = ["http:", "https:", "mailto:", "tel:", "sms:", "tg:"];
      if (!externalProtocols.includes(url.protocol)) return;
      const internalPath = ["http:", "https:"].includes(url.protocol)
        ? getInternalPathFromAppUrl(url.href)
        : null;
      if (internalPath) {
        // Absolute VaultGrid links rendered by the API/email content must stay
        // inside the bundled SPA. Letting the WebView follow them performs a
        // full production-page reload and can reintroduce sticky launch state.
        event.preventDefault();
        const destination = normalizeAttendanceClaimPath(internalPath);
        void prepareProtectedChatNavigation(destination).then(() => {
          if (!disposed) navigateRef.current(destination);
        });
        return;
      }
      if (
        ["http:", "https:"].includes(url.protocol) &&
        url.origin === window.location.origin
      ) {
        return;
      }

      event.preventDefault();
      void openExternalUrl(url.href).catch((error) => {
        console.warn("Failed to open external link", error);
      });
    };
    document.addEventListener("click", handleExternalLinkClick, true);

    return () => {
      disposed = true;
      window.removeEventListener("vg:push-notification-tapped", handlePushTap);
      document.removeEventListener("click", handleExternalLinkClick, true);
      handles.forEach((handle) => handle?.remove?.());
    };
  }, []);
}
